import { useState, useEffect, useRef } from "react";
import { useFocusTimer, useSetTimeout } from "scripts/hooks/timers";
import { ReactNode } from "react";
import { RemoveFilterButton } from "components/RemoveFilterButton";
import { IItem } from "./Typeahead.def";

interface ITypeaheadProps<T> {
    unSelected: () => void;
    selectedValue: string;
    items: Array<T>;
    onSearch: (text: string, identifier: string) => void;
    onSelect: (item: T) => void;
    renderItem: (item: T) => ReactNode;
    identifier: string;
    resetSchoolList: () => void;
    /** Input id, so a `<label htmlFor>` can point at it. */
    id?: string;
    placeholder?: string;
    /** Characters needed before a search runs. */
    minLength?: number;
}

/** Search-as-you-type picker: shows `items` while typing, then the picked value
 *  as a removable chip. `T` is the item shape (`mytype: "None"` marks no match). */
export function Typeahead<T extends { mytype?: string } = IItem>(props: ITypeaheadProps<T>) {
    const {
        items,
        onSearch,
        onSelect,
        renderItem,
        selectedValue,
        unSelected,
        identifier,
        resetSchoolList,
        id,
        placeholder = "Type here to search",
        minLength = 2,
    } = props;

    const [search, setSearch] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    // The text the last search was sent for, i.e. the query `items` answer.
    const [searchedText, setSearchedText] = useState("");
    const [hasFocus, handleFocus, handleBlur] = useFocusTimer();
    // Cleared on unmount, so closing the modal mid-debounce sends no search.
    const timers = useSetTimeout();
    const debounceRef = useRef<number | null>(null);

    // Results are shown (and Enter picks one) only while they answer what the box
    // says: not during the debounce after an edit, nor when a response for
    // earlier text lands.
    const resultsCurrent = isOpen && !isLoading && searchedText === search;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case "ArrowDown":
                setSelectedIndex(Math.min(selectedIndex + 1, items.length - 1));
                break;
            case "ArrowUp":
                setSelectedIndex(Math.max(selectedIndex - 1, 0));
                break;
            case "Enter":
                if (resultsCurrent && items.length > 0 && items[0]?.mytype !== "None") {
                    handleSelect(items[selectedIndex]);
                }
                break;
        }
    };

    const triggerSearch = (value: string) => {
        setSearchedText(value);
        setIsLoading(true);
        onSearch(value, identifier);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        // Any edit cancels the pending search; one is scheduled below if the text still qualifies.
        if (debounceRef.current !== null) {
            timers.clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (value === "") {
            resetSchoolList();
            setIsOpen(false);
            setIsLoading(false);
            return;
        }
        // close list when input too short
        if (value.length < minLength) {
            setIsOpen(false);
            setIsLoading(false);
            return;
        }

        setIsOpen(true);
        debounceRef.current = timers.setTimeout(() => triggerSearch(value), 500);
    };

    useEffect(() => {
        // If we were loading and results changed (or call finished with empty array), stop loading
        setIsLoading(false);
        setSelectedIndex(0);
    }, [items]);

    const handleSelect = (item: T) => {
        onSelect(item);
        setIsOpen(false);
        setSearch("");
        setIsLoading(false);
    };

    return (
        <div>
            {!selectedValue ? (
                <>
                    <input
                        type="text"
                        id={id}
                        autoComplete="off"
                        value={search}
                        onChange={handleSearchChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        className="form-control form-control--small mb-3"
                        placeholder={placeholder}
                    />

                    {hasFocus && isLoading && <div>Loading…</div>}

                    {hasFocus && resultsCurrent && items.length > 0 && items[0]?.mytype !== 'None' && (
                        <ul className="typeahead-scrollbar">
                            {items.map((item, index) => (
                                <li
                                    key={index}
                                    className={
                                        index === selectedIndex
                                            ? "typeahead selected cursor--pointer"
                                            : "typeahead cursor--pointer"
                                    }
                                    onMouseDown={() => handleSelect(item)}
                                >
                                    <div className="typeahead__item p-3 mb-0">
                                        {renderItem(item)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {hasFocus && resultsCurrent && items.length === 1 && items[0]?.mytype === 'None' && (
                        <div>No data found</div>
                    )}
                </>
            ) : (
                <div className="school-typeahead-pill filterpill--closeable mb-3">
                    <span className="white-space-nowrap mr-1">{selectedValue}</span>
                    <RemoveFilterButton label={selectedValue} onRemove={unSelected} style={{ top: 10 }} />
                </div>
            )}
        </div>
    );
}
